import { Immutable } from "immer";
import React from "react";

import type { StearnsGalleryBlock as Value } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { getSpacingClasses } from "../spacing";

type Props = Immutable<{
    value: Value;
}>;

/**
 * Faithful port of the legacy server-rendered `gallery-block.html`. The
 * thumbnail-swap interactivity is owned here via `selected` state: clicking a
 * thumbnail selects it, driving the `.selected` highlight on that thumbnail and
 * swapping the main `<img>` to the selected item's full-size (`original`)
 * rendition. The first thumbnail is selected by default, so the main `<img>`
 * initially shows the first item's original, matching the legacy
 * `{% if forloop.first %}` render.
 */
export const GalleryBlock = (props: Props): React.JSX.Element => {
    const v = props.value;
    const gallery = v.gallery ?? [];
    const [selected, setSelected] = React.useState(0);
    return (
        <div
            className={concatClassNames([
                "gallery",
                getSpacingClasses(v.spacing),
            ])}
        >
            <nav>
                {gallery.map((item, i) => (
                    // Index key: ListBlock items don't surface a stable id, so a
                    // CMS reorder can reuse the wrong thumbnail DOM. Matches the
                    // legacy `forloop`-positional render.
                    <img
                        key={i}
                        alt={item.default_alt_text}
                        className={i === selected ? "selected" : undefined}
                        src={item.renditions["width-150"].url}
                        loading="lazy"
                        decoding="async"
                        onClick={(event) => {
                            event.preventDefault();
                            setSelected(i);
                        }}
                    />
                ))}
            </nav>
            {/* The legacy template only emitted the main `<img>` when the
                gallery had a first item; an empty gallery rendered just the
                `<div>` + empty `<nav>`. Guard on length (not on the item, which
                the index-access type treats as always-defined) to reproduce
                that without a spurious always-truthy lint warning. */}
            {gallery.length > 0 && (
                <img
                    alt={gallery[selected].default_alt_text}
                    src={gallery[selected].renditions["original"].url}
                    loading="lazy"
                    decoding="async"
                />
            )}
        </div>
    );
};
